(() => {
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  class SaCollectionCountdown extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.sa-collection-promo--countdown');
      const expiresAt = Number(this.dataset.expiresAt);
      this.expiresAt = Number.isFinite(expiresAt) && expiresAt > 0
        ? expiresAt * 1000
        : Date.parse(this.dataset.expires || '');

      this.parts = {
        days: this.querySelector('[data-sa-countdown-days]'),
        hours: this.querySelector('[data-sa-countdown-hours]'),
        mins: this.querySelector('[data-sa-countdown-mins]'),
        secs: this.querySelector('[data-sa-countdown-secs]'),
      };

      if (
        !Number.isFinite(this.expiresAt) ||
        Object.values(this.parts).some((part) => !part)
      ) {
        this.complete();
        return;
      }

      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
    }

    tick() {
      const diff = this.expiresAt - Date.now();
      if (diff <= 0) {
        this.complete();
        return;
      }

      const dayMs = 86400000;
      const hourMs = 3600000;
      const minMs = 60000;

      this.parts.days.textContent = String(Math.floor(diff / dayMs)).padStart(2, '0');
      this.parts.hours.textContent = String(
        Math.floor((diff % dayMs) / hourMs),
      ).padStart(2, '0');
      this.parts.mins.textContent = String(
        Math.floor((diff % hourMs) / minMs),
      ).padStart(2, '0');
      this.parts.secs.textContent = String(
        Math.floor((diff % minMs) / 1000),
      ).padStart(2, '0');
    }

    complete() {
      if (this.interval) {
        window.clearInterval(this.interval);
        this.interval = null;
      }
      if (this.root) {
        this.root.hidden = true;
        this.root.classList.add('is-expired');
      }
    }
  }

  const getCollectionRoot = (el) =>
    el?.closest?.('[data-sa-collection]') ||
    document.querySelector('[data-sa-collection]');

  const getDrawer = (fromEl) => {
    const root = getCollectionRoot(fromEl);
    return (
      document.querySelector('.sa-collection__drawer[data-sa-filter-drawer]') ||
      root?.querySelector('[data-sa-filter-drawer]') ||
      null
    );
  };

  const openDrawer = (fromEl) => {
    const root = getCollectionRoot(fromEl);
    const drawer = getDrawer(fromEl);
    if (!drawer) return;

    // Keep Tailwind scope if present; avoid fixed being trapped by ancestors
    drawer.classList.add('css-layout');
    if (drawer.parentElement !== document.body) {
      drawer._saHomeParent = drawer.parentElement;
      drawer._saHomeNext = drawer.nextSibling;
      document.body.appendChild(drawer);
    }

    drawer.hidden = false;
    drawer.removeAttribute('hidden');
    root?.querySelector('[data-sa-filter-open]')?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      document.documentElement.classList.add('sa-collection-filter-open');
    });
  };

  const closeDrawer = (fromEl) => {
    const root = getCollectionRoot(fromEl);
    const drawer = getDrawer(fromEl);
    if (!drawer) return;

    drawer.classList.remove('is-open');
    root?.querySelector('[data-sa-filter-open]')?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('sa-collection-filter-open');

    window.setTimeout(() => {
      if (drawer.classList.contains('is-open')) return;
      drawer.hidden = true;

      const home = drawer._saHomeParent;
      if (home && drawer.parentElement === document.body) {
        if (drawer._saHomeNext && drawer._saHomeNext.parentNode === home) {
          home.insertBefore(drawer, drawer._saHomeNext);
        } else {
          home.appendChild(drawer);
        }
      }
    }, 300);
  };

  class SaCollectionFilters extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.desktopForm = this.querySelector('[data-sa-filters-desktop]');
      this.mobileForm =
        document.querySelector('[data-sa-filters-mobile]') ||
        this.querySelector('[data-sa-filters-mobile]');
    }

    get drawer() {
      return getDrawer(this);
    }

    get openBtn() {
      return this.querySelector('[data-sa-filter-open]');
    }

    openDrawer() {
      openDrawer(this);
    }

    closeDrawer() {
      closeDrawer(this);
    }

    onChange(event) {
      const form = event.target.closest('form');
      if (!form) return;

      // Desktop + mobile: apply immediately (theme default). APPLY only closes the drawer.
      if (
        form === this.desktopForm ||
        form.matches('[data-sa-filters-desktop]') ||
        form === this.mobileForm ||
        form.matches('[data-sa-filters-mobile]')
      ) {
        if (form.matches('[data-sa-filters-desktop]')) this.desktopForm = form;
        if (form.matches('[data-sa-filters-mobile]')) this.mobileForm = form;
        const sourceFilter = event.target.closest('[data-filter]');
        this.submitForm(form, {
          sourceFilterIndex: sourceFilter?.getAttribute('data-index') || '',
        });
      }
    }

    closeAllDropdowns() {
      this.querySelectorAll('[data-sa-filter-dropdown].is-open').forEach((el) => {
        el.classList.remove('is-open');
        el.querySelector('[data-sa-filter-toggle]')?.setAttribute(
          'aria-expanded',
          'false',
        );
      });
    }

    buildUrl(form) {
      const searchParams = new URLSearchParams(new FormData(form));
      const url = new URL(form.action || window.location.href);

      url.search = '';
      searchParams.forEach((value, key) => {
        if (value !== '') url.searchParams.append(key, value);
      });

      ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
        if (url.searchParams.get(key) === '') url.searchParams.delete(key);
      });

      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    submitForm(form, { closeDrawerAfter = false, sourceFilterIndex = '' } = {}) {
      if (!form) return;
      const url = this.buildUrl(form);
      this.renderSection(url, { sourceFilterIndex }).then(() => {
        if (closeDrawerAfter) closeDrawer(this);
      });
    }

    async renderSection(url, { sourceFilterIndex = '' } = {}) {
      const key = url.toString();
      const root = getCollectionRoot(this);
      const drawerOpen = Boolean(getDrawer(this)?.classList.contains('is-open'));
      if (root && !drawerOpen) root.classList.add('is-loading');

      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      try {
        let html = this.cache.get(key);
        if (!html) {
          const response = await fetch(key, { signal });
          html = await response.text();
          this.cache.set(key, html);
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextRoot = doc.querySelector('[data-sa-collection]');
        if (!nextRoot || !root) return;

        const replaceIds = [
          'SaCollectionGrid',
          'SaCollectionDesktopFilters',
          'SaCollectionProductCount',
          'SaCollectionActiveFilters',
        ];

        // Keep mobile drawer form intact while open so inputs/accordions don't reset.
        if (!drawerOpen) replaceIds.push('SaCollectionMobileFilters');

        replaceIds.forEach((id) => {
          const current = document.getElementById(id);
          const next = doc.getElementById(id);
          if (current && next) current.innerHTML = next.innerHTML;
        });

        if (drawerOpen) this.syncMobileFilters(doc, sourceFilterIndex);

        this.desktopForm = this.querySelector('[data-sa-filters-desktop]');
        this.mobileForm =
          document.querySelector('[data-sa-filters-mobile]') ||
          this.querySelector('[data-sa-filters-mobile]');

        const cleanUrl = new URL(url);
        cleanUrl.searchParams.delete('section_id');
        history.replaceState({}, '', cleanUrl.toString());

        const countEl = document.querySelector('[data-sa-product-count-value]');
        const countText = countEl?.textContent.trim() || '';
        if (countEl) this.dataset.resultCount = countText;

        const applyCount = document.querySelector('[data-sa-apply-count]');
        if (applyCount && countText) applyCount.textContent = countText;
      } catch (error) {
        if (error?.name === 'AbortError') return;
        throw error;
      } finally {
        if (root) root.classList.remove('is-loading');
        this.closeAllDropdowns();
      }
    }

    // Update other mobile filter panels without replacing the one just changed.
    syncMobileFilters(doc, sourceFilterIndex = '') {
      const nextFilters = doc.querySelectorAll(
        '#SaCollectionMobileFilters [data-filter]',
      );
      nextFilters.forEach((element) => {
        const index = element.getAttribute('data-index');
        if (!index || index === sourceFilterIndex) return;
        const current = document.querySelector(
          `#SaCollectionMobileFilters [data-filter][data-index="${index}"]`,
        );
        if (!current) return;

        if (current.tagName === 'DETAILS') {
          const nextBody = element.querySelector('summary + *');
          const currentBody = current.querySelector('summary + *');
          if (nextBody && currentBody) currentBody.innerHTML = nextBody.innerHTML;
          return;
        }

        current.innerHTML = element.innerHTML;
      });
    }
  }

  const getFiltersHost = (fromEl) =>
    getCollectionRoot(fromEl)?.querySelector('sa-collection-filters') ||
    document.querySelector('sa-collection-filters');

  // Document-level listeners: drawer is moved to <body>, so events won't bubble into the host.
  document.addEventListener('change', (event) => {
    const target =
      event.target instanceof Element
        ? event.target
        : event.target?.parentElement;
    if (!target) return;

    const form = target.closest(
      'form[data-sa-filters-desktop], form[data-sa-filters-mobile]',
    );
    if (!form) return;

    const host = getFiltersHost(form) || getFiltersHost();
    if (!host || typeof host.onChange !== 'function') return;
    host.onChange(event);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector(
      '.sa-collection__drawer[data-sa-filter-drawer].is-open',
    );
    if (drawer) {
      closeDrawer(drawer);
      return;
    }
    document
      .querySelectorAll('sa-collection-filters')
      .forEach((host) => host.closeAllDropdowns?.());
  });

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;
      if (!target) return;

      if (target.closest('[data-sa-filter-open]')) {
        event.preventDefault();
        openDrawer(target);
        return;
      }

      if (target.closest('[data-sa-filter-close]')) {
        event.preventDefault();
        closeDrawer(target);
        return;
      }

      if (target.closest('[data-sa-filter-apply]')) {
        event.preventDefault();
        // Blur first so pending price inputs fire `change` → apply, then close.
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        closeDrawer(target);
        return;
      }

      const removeLink = target.closest('[data-sa-filter-remove]');
      if (removeLink) {
        event.preventDefault();
        const host = getFiltersHost(removeLink);
        if (!host || typeof host.renderSection !== 'function') {
          window.location.href = removeLink.href;
          return;
        }
        const url = new URL(removeLink.href, window.location.origin);
        url.searchParams.set('section_id', host.sectionId);
        host.renderSection(url);
        return;
      }

      const toggle = target.closest('[data-sa-filter-toggle]');
      if (toggle) {
        event.preventDefault();
        const host = toggle.closest('sa-collection-filters');
        const dropdown = toggle.closest('[data-sa-filter-dropdown]');
        const wasOpen = dropdown?.classList.contains('is-open');
        host?.closeAllDropdowns?.();
        if (dropdown && !wasOpen) {
          dropdown.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
        return;
      }

      // Close desktop dropdowns when clicking outside
      if (!target.closest('[data-sa-filter-dropdown]')) {
        document
          .querySelectorAll('sa-collection-filters')
          .forEach((host) => host.closeAllDropdowns?.());
      }
    },
    true,
  );

  const scrollActiveNavIntoView = () => {
    document
      .querySelectorAll('[data-sa-collection] .sa-collection__nav-scroll')
      .forEach((scroller) => {
        const active = scroller.querySelector('[aria-current="page"]');
        if (!active || window.matchMedia('(min-width: 1000px)').matches) return;

        const target =
          active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2;
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        scroller.scrollTo({
          left: Math.max(0, Math.min(target, maxScroll)),
          behavior: 'auto',
        });
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scrollActiveNavIntoView);
  } else {
    scrollActiveNavIntoView();
  }

  define('sa-collection-countdown', SaCollectionCountdown);
  define('sa-collection-filters', SaCollectionFilters);
})();
